import { useGetFeatures } from '@/common/integrations/menhir/employee/features/features.hooks';
import {
  TInsuranceDuplicatedMember,
  TInsurancePerson,
  TInsuranceUpgradePlan,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import {
  MoneyUtils,
  TMoney,
  defaultMoney,
} from '@coverflex-tech/hypernova-utils';

export const determineInsuranceUpgradeFlags = (
  { persons }: Pick<TInsuranceUpgradePlan, 'persons'>,
  allDuplicatedFamilyMembers: TInsuranceDuplicatedMember[]
) => {
  const { totalEmployeeCost } = calculateTotals(persons);
  const familyMembers = persons.filter((p) => p.type !== 'employee');
  const employeePaysZero = totalEmployeeCost.amount === 0;
  const familyMembersAdded = familyMembers.length > 0;
  const familyMembersIncluded = familyMembers.every((p) => p.included);
  const selectedDuplicatedMembers = allDuplicatedFamilyMembers.reduce<
    TInsuranceDuplicatedMember[]
  >((acc, current) => {
    if (familyMembers.some((fm) => fm.included && fm.id === current.id)) {
      acc.push(current);
    }
    return acc;
  }, []);
  const selectedAlreadyInsuredDuplicatedMembers =
    selectedDuplicatedMembers.filter((dm) => dm.alreadyInsured);
  const hasDuplicatedMembersSelected = selectedDuplicatedMembers.length > 0;
  const hasAlreadyInsuredDuplicatedMembersSelected =
    selectedAlreadyInsuredDuplicatedMembers.length > 0;

  return {
    employeePaysZero,
    familyMembersIncluded,
    familyMembersAdded,
    selectedDuplicatedMembers,
    selectedAlreadyInsuredDuplicatedMembers,
    hasDuplicatedMembersSelected,
    hasAlreadyInsuredDuplicatedMembersSelected,
  };
};

export const shouldShowInsuranceUpgradeConfirmationModal = (
  plan: Pick<TInsuranceUpgradePlan, 'persons'>,
  allDuplicatedFamilyMembers: TInsuranceDuplicatedMember[],
  displayFamilyMembersUI: boolean
) => {
  const { employeePaysZero, familyMembersAdded, hasDuplicatedMembersSelected } =
    determineInsuranceUpgradeFlags(plan, allDuplicatedFamilyMembers);

  return (
    employeePaysZero ||
    (displayFamilyMembersUI &&
      (!familyMembersAdded || hasDuplicatedMembersSelected))
  );
};

export const calculatePersonCost = (person: TInsurancePerson): TMoney => {
  return MoneyUtils.sum([person.paidByEmployee, person.paidByCompany]);
};

export const calculateTotals = (
  persons: TInsurancePerson[],
  filterFn: (p: TInsurancePerson) => boolean = () => true
) => {
  return persons.filter(filterFn).reduce(
    (acc, person) => ({
      totalCompanyCost: MoneyUtils.sum([
        acc.totalCompanyCost,
        person.included ? person.paidByCompany : defaultMoney,
      ]),
      totalEmployeeCost: MoneyUtils.sum([
        acc.totalEmployeeCost,
        person.included ? person.paidByEmployee : defaultMoney,
      ]),
      totalCost: MoneyUtils.sum([
        acc.totalCost,
        person.included ? person.paidByEmployee : defaultMoney,
        person.included ? person.paidByCompany : defaultMoney,
      ]),
    }),
    {
      totalEmployeeCost: defaultMoney,
      totalCompanyCost: defaultMoney,
      totalCost: defaultMoney,
    }
  );
};

export const useShouldDisplayFamilyMembersUI = () => {
  const {
    features: { FF_CAN_ADD_FAMILY_MEMBERS },
  } = useGetFeatures();

  return {
    shouldDisplayFamilyMembersUI: ({
      familyMembersAllowed,
      canAddFamilyMembers,
    }: Partial<
      Pick<
        TInsuranceUpgradePlan,
        'familyMembersAllowed' | 'canAddFamilyMembers'
      >
    > = {}) =>
      Boolean(
        familyMembersAllowed &&
          (!FF_CAN_ADD_FAMILY_MEMBERS || canAddFamilyMembers)
      ),
  };
};
